<template>
  <nav
    class="nav"
    data-ga-nav="header"
    :class="{
      'nav--subpage': variant === 'subpage',
      'nav--menu-active': menuActive,
      'nav--scrolled': scrolled,
    }"
  >
    <a
      v-if="variant === 'subpage'"
      href="/"
      class="nav-back"
      :aria-label="trans.get('landing.nav.back_to_home_aria')"
    >
      <icon-chevron-left :stroke-width="2.4" />
      <span class="nav-back-text">{{ trans.get('landing.nav.back') }}</span>
    </a>

    <a
      href="/"
      class="logo"
      :aria-label="trans.get('landing.nav.home_aria')"
    >
      <!-- On subpages viluu-site only shows the wordmark (no app icon), so we
           hide the icon there to keep the slimmer look. -->
      <img
        v-if="variant !== 'subpage'"
        src="../../../../../images/viluu/landing-v2/viluu-appicon.svg"
        :alt="trans.get('landing.common.brand_alt')"
        width="38"
        height="38"
        class="logo-appicon"
      />
      <img
        src="../../../../../images/viluu/landing-v2/viluu-wordmark.svg"
        :alt="trans.get('landing.common.wordmark_alt')"
        width="54"
        height="22"
        class="logo-wordmark"
      />
    </a>

    <div class="nav-links">
      <a
        v-for="link in resolvedLinks"
        :key="link.key"
        :href="link.href"
        data-ga-nav-link
        :class="{ active: link.active }"
        >{{ trans.get(link.labelKey) }}</a
      >
    </div>

    <div class="nav-actions">
      <button
        class="btn-text"
        type="button"
        data-cta-location="header"
        @click="open('login')"
      >
        {{ trans.get('landing.nav.login') }}
      </button>
      <button
        class="btn-pill"
        type="button"
        data-cta-location="header"
        @click="open('register')"
      >
        {{ trans.get('landing.nav.register_free')
        }}<span
          v-if="variant === 'subpage'"
          aria-hidden="true"
        >
          →</span
        >
      </button>
    </div>

    <button
      class="nav-burger"
      type="button"
      :aria-label="
        menuOpen
          ? trans.get('landing.nav.menu_close_aria')
          : trans.get('landing.nav.menu_open_aria')
      "
      :aria-expanded="menuOpen ? 'true' : 'false'"
      aria-controls="landing-nav-mobile-menu"
      @click="toggleMenu"
    >
      <span
        class="nav-burger-box"
        :class="{ open: menuOpen }"
        aria-hidden="true"
      >
        <span class="nav-burger-bar"></span>
        <span class="nav-burger-bar"></span>
        <span class="nav-burger-bar"></span>
      </span>
    </button>

    <transition
      name="landing-nav-mobile"
      @after-leave="onMenuLeft"
    >
      <div
        v-if="menuOpen"
        id="landing-nav-mobile-menu"
        class="landing-nav-mobile"
        :class="{ 'landing-nav-mobile--instant': instantClose }"
        @click.self="closeMenu"
      >
        <div
          class="landing-nav-mobile-sheet"
          role="dialog"
          aria-modal="true"
          :aria-label="trans.get('landing.nav.menu_label')"
        >
          <a
            v-for="link in resolvedLinks"
            :key="link.key"
            :href="link.href"
            class="landing-nav-mobile-link"
            data-ga-nav-link
            :class="{ active: link.active }"
            @click="closeMenu"
            >{{ trans.get(link.labelKey) }}</a
          >
          <div class="landing-nav-mobile-actions">
            <button
              class="landing-nav-mobile-login"
              type="button"
              data-cta-location="header"
              @click="openFromMenu('login')"
            >
              {{ trans.get('landing.nav.login') }}
            </button>
            <button
              class="landing-nav-mobile-cta"
              type="button"
              data-cta-location="header"
              @click="openFromMenu('register')"
            >
              {{ trans.get('landing.nav.register_free') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
// Single source of truth for the primary menu — identical on the landing and
// every subpage so the navigation stays consistent site-wide. `key` is a stable
// identifier for the active marker, decoupled from the href so changing a route
// never silently breaks the underline.
const NAV_LINKS = [
  { key: 'home', href: 'home', labelKey: 'landing.nav.home' },
  { key: 'how-it-works', href: 'so-funktioniert', labelKey: 'landing.nav.how_it_works' },
  { key: 'stories', href: 'stories', labelKey: 'landing.nav.stories' },
  { key: 'about', href: 'ueber-uns', labelKey: 'landing.nav.about' },
  { key: 'help', href: 'faq', labelKey: 'landing.nav.help' },
];

import IconChevronLeft from '../icons/IconChevronLeft.vue';

export default {
  name: 'TheNav',
  components: { IconChevronLeft },
  props: {
    variant: { type: String, default: 'landing' },
    activeKey: { type: String, default: null },
  },
  data() {
    return {
      menuOpen: false,
      menuActive: false,
      scrolled: false,
      instantClose: false,
    };
  },
  computed: {
    resolvedActiveKey() {
      if (this.activeKey !== null) {
        return this.activeKey;
      }
      return this.variant === 'subpage' ? null : 'home';
    },
    resolvedLinks() {
      return NAV_LINKS.map((link) => ({ ...link, active: link.key === this.resolvedActiveKey }));
    },
  },
  watch: {
    menuOpen(open) {
      document.body.classList.toggle('nav-menu-open', open);
      if (open) {
        this.menuActive = true;
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onScroll);
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
    }
    document.body.classList.remove('nav-menu-open');
  },
  methods: {
    open(mode) {
      this.$eventHub.$emit('viluu-landing:open-signup', {
        mode,
        cta_location: 'header',
      });
    },
    onScroll() {
      // rAF-throttle so a noisy scroll stream collapses to one read per frame.
      if (this.scrollRaf) {
        return;
      }
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        // When a modal/menu pins the body (position: fixed), window.scrollY
        // reads 0 and fires a bogus scroll event — keep the last state so the
        // bar doesn't lose its frosted background while a modal is open.
        if (
          document.body.classList.contains('signup-locked') ||
          document.body.classList.contains('nav-menu-open')
        ) {
          return;
        }
        this.scrolled = (window.scrollY || window.pageYOffset || 0) > 8;
      });
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    openFromMenu(mode) {
      // Close instantly so the menu doesn't linger/fade out on top of the modal.
      // The instant class must be on the element *before* it starts leaving —
      // <transition> removes the pre-leave vnode, so flipping instantClose in the
      // same tick as menuOpen wouldn't reach it. Set it, let the (still-open) menu
      // re-render with the class, then close on the next tick.
      this.instantClose = true;
      this.$nextTick(() => {
        this.closeMenu();
        this.open(mode);
      });
    },
    onMenuLeft() {
      // Runs after the menu's leave transition (instant or animated).
      this.menuActive = false;
      this.instantClose = false;
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.menuOpen) {
        this.closeMenu();
      }
    },
  },
};
</script>
<style scoped lang="scss">
.nav {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 40px;
  gap: 14px;
  // backdrop-filter eases in slowly so the frosted blur fades up gently after
  // the mobile menu closes (the menu disables it, see .nav--menu-active below;
  // browsers interpolate none → blur as blur(0) → blur).
  transition: background 0.25s ease, box-shadow 0.25s ease,
    backdrop-filter 0.6s ease, -webkit-backdrop-filter 0.6s ease;
}

// Opaque bar behind the logo + close button, shown while the mobile menu is
// active (kept through the close animation via .nav--menu-active). Sits above
// the menu overlay (z 50) but below the logo / burger (z 60) so the sheet
// retracts behind it instead of sliding visibly under the logo.
.nav::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 55;
  background: #fffaf7;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  // Fade out in step with the backdrop blur (0.6s) when the menu closes, so the
  // bar goes white → frosted without flashing the sharp content underneath.
  transition: opacity 0.6s ease, visibility 0s linear 0.6s;
}

.nav--menu-active::before {
  visibility: visible;
  opacity: 1;
  // Appear instantly when the menu opens — it has to mask straight away.
  transition: none;
}

.nav.nav--subpage {
  max-width: 1480px;
  margin: 0 auto;
  // Subpages keep viluu-site's wider 40px gutter (the home-only 10px inset above
  // doesn't apply once the bar is centred in a max-width container). Layout
  // stays space-between + logo margin-right:auto, exactly like viluu-site.
  padding: 22px 40px;
}

.nav.nav--subpage .logo {
  margin-right: auto;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--display);
  font-weight: 700;
  font-size: 28px;
  color: var(--ink);
  letter-spacing: -0.02em;
  // Kept above the open menu overlay (z-index 50) so the brand stays visible
  // alongside the "X", matching the closed bar.
  position: relative;
  z-index: 60;
}

.logo img {
  display: block;
  flex-shrink: 0;
}


.logo img.logo-appicon {
  width: 38px;
  height: 38px;
}

.logo img.logo-wordmark {
  width: 54px;
  height: 22px;
}

.nav.nav--subpage .logo img.logo-wordmark {
  height: 24px;
}

.nav-links {
  display: flex;
  gap: 32px;
  font-size: 14px;
  font-weight: 500;
  color: var(--ink-dim);
  line-height: normal;
}

.nav-links a {
  color: inherit;
  text-decoration: none;
  position: relative;
  transition: color 0.2s;
}

.nav-links a:hover {
  color: var(--ink);
}

.nav-links a.active {
  color: var(--ink);
}

.nav-links a.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 2px;
  background: var(--pink);
  border-radius: 2px;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-text {
  background: none;
  border: none;
  font: inherit;
  font-weight: 600;
  color: var(--ink);
  cursor: pointer;
  padding: 10px 14px;
}

.nav.nav--subpage .btn-text {
  text-decoration: underline;
}

.btn-pill {
  font: inherit;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  background: var(--brand-grad);
  border: none;
  padding: 12px 22px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 8px 20px -6px rgba(255, 59, 127, 0.55);
  transition: transform 0.2s, box-shadow 0.2s;
  white-space: nowrap;
  line-height: normal;
}

.btn-pill:hover {
  box-shadow: 0 14px 30px -8px rgba(255, 59, 127, 0.65);
}

.nav-back {
  // Above the menu masking bar (z 55), like the logo / burger.
  position: relative;
  z-index: 60;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--sans);
  font-weight: 500;
  font-size: 13px;
  color: var(--ink-dim);
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--line);
  padding: 8px 14px 8px 10px;
  border-radius: 999px;
  text-decoration: none;
  transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
}

.nav-back:hover {
  background: #fff;
  color: var(--ink);
  transform: translateX(-2px);
  box-shadow: 0 4px 12px -4px rgba(26, 12, 20, 0.15);
}

.nav-back svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* ===== Mobile hamburger ===== */

.nav-burger {
  display: none; // revealed at the mobile breakpoint below
  // Sits above the open menu overlay (z-index 50) so it stays visible and
  // tappable as the "X" close affordance once the sheet is open.
  position: relative;
  z-index: 60;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.2s ease;
}

.nav-burger:hover {
  background: rgba(26, 12, 20, 0.06);
}

.nav-burger-box {
  position: relative;
  width: 22px;
  height: 16px;
}

.nav-burger-bar {
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  border-radius: 2px;
  background: currentColor;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease, top 0.3s ease;
}

.nav-burger-bar:nth-child(1) {
  top: 0;
}
.nav-burger-bar:nth-child(2) {
  top: 7px;
}
.nav-burger-bar:nth-child(3) {
  top: 14px;
}

// Collapse the three bars into an X.
.nav-burger-box.open .nav-burger-bar:nth-child(1) {
  top: 7px;
  transform: rotate(45deg);
}
.nav-burger-box.open .nav-burger-bar:nth-child(2) {
  opacity: 0;
}
.nav-burger-box.open .nav-burger-bar:nth-child(3) {
  top: 7px;
  transform: rotate(-45deg);
}

/* ===== Mobile menu sheet ===== */

.landing-nav-mobile {
  position: fixed;
  inset: 0;
  z-index: 50;
  // Same backdrop as the signup/login modal on mobile (solid dark, no blur).
  background: rgba(15, 5, 12, 0.85);
}

.landing-nav-mobile-sheet {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: 80px 18px 24px; // top padding clears the bar + burger
  background: #fffaf7;
  border-radius: 0 0 28px 28px;
  box-shadow: 0 30px 60px -20px rgba(20, 5, 15, 0.4);
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.landing-nav-mobile-link {
  font-family: var(--sans);
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  padding: 15px 10px;
  border-radius: 12px;
  transition: background 0.2s ease, color 0.2s ease;
}

.landing-nav-mobile-link + .landing-nav-mobile-link {
  border-top: 1px solid var(--line);
}

.landing-nav-mobile-link:hover {
  background: rgba(26, 12, 20, 0.05);
}

.landing-nav-mobile-link.active {
  color: var(--pink);
}

.landing-nav-mobile-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
}

.landing-nav-mobile-login,
.landing-nav-mobile-cta {
  font: inherit;
  font-weight: 600;
  font-size: 15px;
  width: 100%;
  padding: 14px 20px;
  border-radius: 999px;
  cursor: pointer;
  line-height: normal;
}

.landing-nav-mobile-login {
  background: #fff;
  border: 1px solid var(--line);
  color: var(--ink);
  transition: background 0.2s ease;
}

.landing-nav-mobile-login:hover {
  background: rgba(26, 12, 20, 0.04);
}

.landing-nav-mobile-cta {
  border: none;
  color: #fff;
  background: var(--brand-grad);
  box-shadow: 0 8px 20px -6px rgba(255, 59, 127, 0.55);
  transition: box-shadow 0.2s ease;
}

.landing-nav-mobile-cta:hover {
  box-shadow: 0 14px 30px -8px rgba(255, 59, 127, 0.65);
}

// Backdrop fade + sheet slide, driven by the <transition name="landing-nav-mobile">.
.landing-nav-mobile-enter-active,
.landing-nav-mobile-leave-active {
  // Slightly longer than the sheet slide (0.32s) so @after-leave fires only
  // once the sheet is fully retracted, then the masking bar is cleared.
  transition: opacity 0.34s ease;
}

// Backdrop fades only on leave; on enter it appears instantly so the
// click-to-close overlay is there right away (the sheet still slides in).
.landing-nav-mobile-leave-to {
  opacity: 0;
}

.landing-nav-mobile-enter .landing-nav-mobile-sheet,
.landing-nav-mobile-leave-to .landing-nav-mobile-sheet {
  transform: translateY(-100%);
}

// Instant close when the menu hands off to a signup/login modal — no slide/fade.
.landing-nav-mobile--instant,
.landing-nav-mobile--instant .landing-nav-mobile-sheet {
  transition-duration: 0s;
}

// Staggered reveal of the links + action buttons. `both` holds them hidden
// during the delay, so each item only drops in once the sheet has expanded.
// The downward drop echoes the top-sheet's opening direction.
@keyframes navMenuItemIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.landing-nav-mobile-sheet > * {
  animation: navMenuItemIn 0.42s cubic-bezier(0.22, 1, 0.36, 1) both;
}

// Start after the sheet is mostly open, then cascade. Covers the current
// 5 links + actions row; a 6th link would still appear, just without a
// stagger delay — bump the loop bound if the menu grows.
@for $i from 1 through 6 {
  .landing-nav-mobile-sheet > *:nth-child(#{$i}) {
    animation-delay: 0.18s + ($i - 1) * 0.05s;
  }
}

@media (prefers-reduced-motion: reduce) {
  .nav-burger-bar,
  .landing-nav-mobile-sheet,
  .landing-nav-mobile-enter-active,
  .landing-nav-mobile-leave-active {
    transition-duration: 0.001s;
  }
  // No fade/drop cascade — show everything immediately.
  .landing-nav-mobile-sheet > * {
    animation: none;
    opacity: 1;
  }
}

@media (max-width: 900px) {
  .nav-links {
    display: none;
  }
  // The inline auth buttons move into the hamburger sheet at this width.
  .nav-actions {
    display: none;
  }
  .nav-burger {
    display: inline-flex;
  }
  .nav.nav--subpage {
    padding: 18px 22px;
    flex-wrap: wrap;
    gap: 12px;
  }

  // Pin the bar to the top so it stays reachable while scrolling.
  .nav {
    position: sticky;
    top: 0;
  }

  // Once scrolled past the top, give the bar a frosted backdrop so content
  // passing underneath stays legible.
  .nav--scrolled {
    background: rgba(255, 250, 247, 0.72);
    -webkit-backdrop-filter: blur(12px) saturate(1.3);
    backdrop-filter: blur(12px) saturate(1.3);
    box-shadow: 0 6px 20px -12px rgba(26, 12, 20, 0.4);
  }

  // A backdrop-filter on the bar would make it the containing block for the
  // fixed menu overlay, collapsing it to the bar's height. While the menu is
  // open the bar is hidden behind the masking band anyway, so drop the filter
  // and let the overlay cover the full viewport (and do its own blur).
  // Crucially this must drop *instantly* (transition-duration: 0s) — the base
  // .nav eases backdrop-filter over 0.6s, and a lingering non-none value would
  // keep the bar a containing block, leaving the overlay collapsed for that long
  // when the menu is opened while scrolled. The slow ease back in happens on
  // close, via the base .nav transition.
  .nav--scrolled.nav--menu-active {
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    transition-duration: 0s;
  }
}

@media (max-width: 720px) {
  .nav:not(.nav--subpage) {
    padding: 16px;
    gap: 5px;
  }
  .nav:not(.nav--subpage) .logo {
    font-size: 24px;
  }
  .nav:not(.nav--subpage) .nav-actions {
    gap: 6px;
  }
  .nav:not(.nav--subpage) .btn-text {
    padding: 8px 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--ink-dim);
  }
  .nav:not(.nav--subpage) .btn-text:hover {
    color: var(--ink);
  }
  .nav:not(.nav--subpage) .btn-pill {
    padding: 10px 16px;
    font-size: 13px;
  }
}

@media (max-width: 560px) {
  .nav-back {
    padding: 9px;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    justify-content: center;
  }
  .nav-back .nav-back-text {
    display: none;
  }
  .nav-back svg {
    width: 16px;
    height: 16px;
  }
}
</style>
